import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './HorizontalScroll.css';
import './ModernButtons.css';
import './EnhancedStyles.css';
import './Navigation.css';
import { ReactComponent as PearLogo } from './logo.svg';
import { Helmet } from 'react-helmet';
import sponsorsData from './data/sponsors.json';
import { FaChevronDown, FaChevronUp, FaInstagram, FaTwitter, FaTwitch, FaYoutube, FaTiktok, FaGamepad, FaSearch, FaRocket, FaChartBar, FaFacebook } from 'react-icons/fa';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import CustomScrollbar from './components/CustomScrollbar';

// API base URL - automatically detects environment
const API_URL = process.env.NODE_ENV === 'production' 
  ? '/api' // In production, the API is served from the same domain
  : 'http://localhost:3003/api'; // In development, use localhost

// 404 Not Found Page Component
function NotFound() {
  return (
    <div className="not-found-page">
      <Helmet>
        <title>404 - Page Not Found | ThirdRail Avenue</title>
        <meta name="description" content="The page you are looking for could not be found." />
      </Helmet>
      
      <div className="not-found-container">
        <h1>404</h1>
        <h2>ERROR: PAGE NOT FOUND</h2>
        <p>This page isn't available.</p>
        <Link to="/" className="go-home-button">Go to Homepage</Link>
      </div>
    </div>
  );
}

// Thank You Page Component
function ThankYou() {
  React.useEffect(() => {
    // Add animation class after component mounts
    const container = document.querySelector('.thank-you-container');
    setTimeout(() => {
      if (container) {
        container.classList.add('animate');
      }
    }, 100);
  }, []);

  return (
    <div className="thank-you-page">
      <Helmet>
        <title>Thank You - ThirdRail Avenue</title>
        <meta name="description" content="Thank you for contacting ThirdRail Avenue." />
      </Helmet>
      
      <div className="thank-you-header">
        <Link to="/" className="thank-you-logo">
          <img 
            src={process.env.PUBLIC_URL + '/Image16.png'} 
            alt="ThirdRail Avenue Logo" 
            style={{ 
              maxHeight: '60px',
              objectFit: 'contain',
              filter: 'drop-shadow(0px 0px 8px rgba(255, 255, 255, 0.3))'
            }}
          />
        </Link>
      </div>
      
      <div className="thank-you-container">
        <div className="success-icon">✓</div>
        <h1>Thank You For Your Message</h1>
        <p>We appreciate you contacting ThirdRail Avenue.<br/>Our team will be in touch with you shortly.</p>
        <Link to="/" className="submit-button">Back to Homepage</Link>
      </div>
      
      <div className="thank-you-footer">
        &copy; {new Date().getFullYear()} ThirdRail Avenue. All Rights Reserved.
      </div>
    </div>
  );
}

// Custom Cursor Component
function CustomCursor() {
  const cursorRef = useRef(null);
  const cursorFollowerRef = useRef(null);

  useEffect(() => {
    // Create main cursor
    const cursor = document.createElement('div');
    cursor.classList.add('cursor');
    document.body.appendChild(cursor);
    cursorRef.current = cursor;

    // Create follower cursor
    const follower = document.createElement('div');
    follower.classList.add('cursor', 'cursor__follower');
    document.body.appendChild(follower);
    cursorFollowerRef.current = follower;

    // Handle cursor movement
    let mouseX = 0;
    let mouseY = 0;
    let cursorX = 0;
    let cursorY = 0;
    let followerX = 0;
    let followerY = 0;

    const animate = () => {
      // Main cursor follows mouse instantly
      cursorX = mouseX;
      cursorY = mouseY;
      cursor.style.transform = `translate3d(${cursorX}px, ${cursorY}px, 0)`;

      // Follower cursor follows with smooth lerp
      followerX += (mouseX - followerX) * 0.2;
      followerY += (mouseY - followerY) * 0.2;
      follower.style.transform = `translate3d(${followerX}px, ${followerY}px, 0)`;

      requestAnimationFrame(animate);
    };

    const handleMouseMove = (e) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
    };

    document.addEventListener('mousemove', handleMouseMove);
    animate();

    // Cleanup on unmount
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.body.removeChild(cursor);
      document.body.removeChild(follower);
    };
  }, []);

  return null;
}

// Talent Roster Page Component
function TalentRoster() {
  const [talents] = useState([
    {
      name: "IAN77",
      image: "/Ian77.png",
      reach: "1.5M+ REACH",
      socials: {
        twitch: "#",
        tiktok: "#",
        twitter: "#"
      }
    },
    {
      name: "RYLEY",
      image: "/Ryley.png",
      reach: "1.2M+ REACH",
      socials: {
        twitch: "#",
        youtube: "#",
        twitter: "#"
      }
    },
    {
      name: "ALT",
      image: "/ALT.png",
      reach: "200K+ REACH",
      socials: {
        youtube: "#",
        twitter: "#",
        instagram: "#"
      }
    },
    {
      name: "VLAKX",
      image: "/vlakx.jpg",
      reach: "950K+ REACH",
      socials: {
        youtube: "#",
        twitter: "#",
        instagram: "#"
      }
    },
    {
      name: "SOLAR FURY",
      image: "/SolarFury.jpg",
      reach: "800K+ REACH",
      socials: {
        youtube: "#",
        twitch: "#",
        twitter: "#"
      }
    }
  ]);

  return (
    <div className="talent-roster-page">
      <CustomCursor />
      <Helmet>
        <title>Our Talent - ThirdRail Avenue</title>
        <meta name="description" content="Meet our exclusive roster of gaming talent at ThirdRail Avenue." />
      </Helmet>
      
      {/* Navigation Bar */}
      <nav className="top-navbar">
        <div className="navbar-logo">
          <Link to="/">
            <img 
              src={process.env.PUBLIC_URL + '/Image16.png'} 
              alt="ThirdRail Avenue Logo" 
            />
          </Link>
        </div>
        <div className="navbar-links">
          <Link to="/">HOME</Link>
          <Link to="/talent-roster">TALENT</Link>
          <Link to="/#services">SERVICES</Link>
          <Link to="/#testimonials">TESTIMONIALS</Link>
          <Link to="/#contact">CONTACT</Link>
        </div>
        <div className="navbar-right">
          <Link to="/#contact" className="contact-button">Contact</Link>
        </div>
      </nav>
      
      {/* Hero Section */}
      <div className="talent-roster-hero">
        <h1>OUR TALENT</h1>
        <p className="talent-roster-subtitle">MEET THE CREATORS CHANGING THE GAMING LANDSCAPE</p>
      </div>
      
      {/* Talent Grid */}
      <div className="talent-roster-grid">
        {talents.map((talent, index) => (
          <div key={index} className="talent-roster-card">
            <div className="talent-roster-image">
              <img src={process.env.PUBLIC_URL + talent.image} alt={talent.name} />
            </div>
            <div className="talent-roster-content">
              <h2 className="talent-roster-name">{talent.name}</h2>
              <p className="talent-roster-reach">{talent.reach}</p>
              <div className="talent-roster-socials">
                {Object.entries(talent.socials).map(([platform, url]) => (
                  <a key={platform} href={url} target="_blank" rel="noopener noreferrer">
                    {platform === "youtube" && <FaYoutube />}
                    {platform === "twitch" && <FaTwitch />}
                    {platform === "twitter" && <FaTwitter />}
                    {platform === "tiktok" && <FaTiktok />}
                    {platform === "instagram" && <FaInstagram />}
                  </a>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MainApp() {
  const [activePage, setActivePage] = useState(0);
  const [activeCardIndex, setActiveCardIndex] = useState(0);
  const [activeSponsorIndex, setActiveSponsorIndex] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
  const [isHovering, setIsHovering] = useState(false);
  const containerRef = useRef(null);
  const carouselIntervalRef = useRef(null);
  const sponsorIntervalRef = useRef(null);
  const sponsorRibbonRef = useRef(null);
  const [formData, setFormData] = useState({
    firstName: '',
    email: '',
    subject: '',
    message: ''
  });
  // Track the last scroll direction and position
  const lastScrollTop = useRef(0);
  const lastScrollTime = useRef(0);
  // Touch handling
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const [activeTestimonialIndex, setActiveTestimonialIndex] = useState(0);
  const testimonialIntervalRef = useRef(null);
  const cursorRef = useRef(null);
  const cursorFollowerRef = useRef(null);

  // List of sponsor logos
  const sponsorLogos = [
    '/sponsors/Image5.png',
    '/sponsors/Image6.png',
    '/sponsors/Image7.png',
    '/sponsors/Image8.png',
    '/sponsors/Image9.png',
    '/sponsors/Image10.png',
    '/sponsors/Image11.png',
    '/sponsors/Image14.png',

  ];

  const pageTitles = ["Home", "Talent", "Services", "Testimonials", "Contact"];

  // Handle wheel events for smooth scrolling
  const handleWheel = (e) => {
    e.preventDefault();
    
    // Prevent scroll if animation is already in progress
    if (isAnimating) return;
    
    // Improved debounce with time and direction tracking
    const now = Date.now();
    const elapsed = now - lastScrollTime.current;
    
    // Minimum time between scroll actions - increased for smoother experience
    if (elapsed < 900) return;
    
    // Detect more intentional scrolling by checking delta magnitude
    const intentionalScroll = Math.abs(e.deltaY) > 30;
    
    if (!intentionalScroll) return;
    
    // Set animation flag to prevent multiple scrolls
    setIsAnimating(true);
    
    if (e.deltaY > 0) {
      // Scrolling down
      handleNextPage();
    } else {
      // Scrolling up
      handlePrevPage();
    }
    
    // Update last scroll time
    lastScrollTime.current = now;
    
    // Clear animation flag after transition completes
    setTimeout(() => {
      setIsAnimating(false);
    }, 1000);
  };

  // Configure page data
  const pages = [
    {
      id: 'page-a',
      videoSrc: '/videos/ref.mp4',
      title: 'THIRDRAIL AVENUE',
      subtitle: 'END-TO-END MARKETING SOLUTIONS',
      description: 'For Creators, Game Developers, and Brands',
      cta: 'EXPLORE SERVICES',
      logo: <PearLogo />
    },
    {
      id: 'talent',
      title: 'Our Exclusive Talent',
      headline: 'THE VOICES THAT DEFINE THIRDRAIL AVENUE',
      subheadline: 'MEET THE CREATORS CHANGING THE GAMING LANDSCAPE',
      content: 'ThirdRail Avenue exclusively represents a select team of influential gaming creators who bring authentic passion and expertise to every collaboration. Our roster includes some of the most engaging personalities in the gaming space.',
      talents: [
        {
          name: 'Ryley',
          image: process.env.PUBLIC_URL + '/Ryley.png',
          followers: '1.2M Followers',
          specialty: 'FPS & Strategy Games',
          socials: [
            { type: "twitch", url: "#" },
            { type: "youtube", url: "#" },
            { type: "twitter", url: "#" }
          ]
        },
        {
          name: 'Vlakx',
          image: process.env.PUBLIC_URL + '/vlakx.jpg',
          followers: '850K Followers',
          specialty: 'RPG & Adventure Games',
          socials: [
            { type: "youtube", url: "#" },
            { type: "twitter", url: "#" },
            { type: "instagram", url: "#" }
          ]
        },
        {
          name: 'Ian77',
          image: process.env.PUBLIC_URL + '/Ian77.png',
          followers: '1.5M Followers',
          specialty: 'Battle Royale & Streaming',
          socials: [
            { type: "twitch", url: "#" },
            { type: "tiktok", url: "#" },
            { type: "twitter", url: "#" }
          ]
        }
      ]
    },
    {
      id: 'what-we-do',
      title: 'WHAT WE DO',
      cards: [
        {
          icon: <FaGamepad size={40} color="#ff0000" />,
          title: 'GAMING CONTENT STRATEGY',
          description: 'We craft targeted content strategies for gaming brands, creators, and publishers seeking to connect with authentic audiences. Our data-driven approach ensures maximum engagement and ROI across all platforms.',
          fixed: true
        },
        {
          icon: <FaSearch size={40} color="#ff0000" />,
          title: 'TALENT MARKETING',
          description: 'Our specialized marketing services for gaming talent helps creators expand their audience, secure brand partnerships, and optimize their content to stand out in the competitive gaming landscape.',
          fixed: true
        },
        {
          icon: <FaRocket size={40} color="#ff0000" />,
          title: 'INHOUSE PRODUCTION',
          description: 'From indie gems to AAA titles, we create comprehensive launch campaigns that build anticipation, drive day-one engagement, and establish long-term community connection for sustainable success.',
          fixed: true
        },
        {
          icon: <FaChartBar size={40} color="#ff0000" />,
          title: 'ANALYTICS & OPTIMIZATION',
          description: 'Our dedicated analytics team continually monitors campaign performance, providing actionable insights and agile optimization to maximize impact and deliver measurable results.',
          fixed: true
        }
      ]
    },
    {
      id: 'success-stories',
      title: 'Success Stories',
      testimonials: [
        {
          name: 'Ian77',
          role: 'Content Creator',
          quote: 'Working with Third Rail has been an amazing experience. They truly understood my needs and made the entire process incredibly smooth. Thanks to their support, I now receive sponsorship opportunities almost every month — a huge improvement compared to before, when I used to get sponsors only every 4–5 months. I highly recommend Third Rail and their services to any YouTubers looking to grow and thrive.',
          image: '/Ian77.png'
        },
        {
          name: 'Ryley',
          role: 'Content Creator',
          quote: 'I have been with TRA for a while now. They handle everything behind the scenes from providing scripts to editing the videos, so I don\'t have to focus on the backend side of things. Thanks to their support, my revenue has grown significantly. I would highly recommend TRA to all content creators out there who would like to boost their earnings and not have to deal with the headache of managing sponsors.',
          image: '/Ryley.png'
        }
      ]
    },
    {
      id: 'contact',
      title: 'Contact',
      isContact: true
    }
  ];

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const windowHeight = window.innerHeight;
      const currentPage = Math.floor(scrollPosition / windowHeight);
      setActivePage(currentPage);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Touch events for mobile swipe
  useEffect(() => {
    const handleTouchStart = (e) => {
      touchStartX.current = e.touches[0].clientX;
      touchStartY.current = e.touches[0].clientY;
    };

    const handleTouchMove = (e) => {
      if (!touchStartX.current || !touchStartY.current) return;

      const touchEndX = e.touches[0].clientX;
      const touchEndY = e.touches[0].clientY;
      
      const xDiff = touchStartX.current - touchEndX;
      const yDiff = touchStartY.current - touchEndY;
      
      // Determine if vertical swipe is more significant than horizontal
      if (Math.abs(yDiff) > Math.abs(xDiff)) {
        if (yDiff > 50) {
          // Swipe up - go to next page
          handleNextPage();
        } else if (yDiff < -50) {
          // Swipe down - go to previous page
          handlePrevPage();
        }
        
        // Reset start position after significant swipe
        touchStartX.current = 0;
        touchStartY.current = 0;
      }
    };

    document.addEventListener('touchstart', handleTouchStart, false);
    document.addEventListener('touchmove', handleTouchMove, false);
    
    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
    };
  }, [activePage]);

  // Auto-rotate talent cards
  useEffect(() => {
    carouselIntervalRef.current = setInterval(() => {
      handleNextCard();
    }, 5000);
    
    return () => clearInterval(carouselIntervalRef.current);
  }, [activeCardIndex]);

  // Auto-rotate sponsor cards
  useEffect(() => {
    sponsorIntervalRef.current = setInterval(() => {
      handleNextSponsor();
    }, 5000);
    
    return () => clearInterval(sponsorIntervalRef.current);
  }, [activeSponsorIndex]);

  // Auto-rotate testimonials
  useEffect(() => {
    testimonialIntervalRef.current = setInterval(() => {
      handleNextTestimonial();
    }, 6000);
    
    return () => clearInterval(testimonialIntervalRef.current);
  }, [activeTestimonialIndex]);

  // Handle sponsor ribbon hover effect
  useEffect(() => {
    const ribbonTrack = document.querySelector('.sponsors-ribbon-track');
    
    const handleMouseEnter = () => {
      if (ribbonTrack) {
        ribbonTrack.style.animationPlayState = 'paused';
      }
    };
    
    const handleMouseLeave = () => {
      if (ribbonTrack) {
        ribbonTrack.style.animationPlayState = 'running';
      }
    };
    
    if (ribbonTrack) {
      ribbonTrack.addEventListener('mouseenter', handleMouseEnter);
      ribbonTrack.addEventListener('mouseleave', handleMouseLeave);
    }
    
    return () => {
      if (ribbonTrack) {
        ribbonTrack.removeEventListener('mouseenter', handleMouseEnter);
        ribbonTrack.removeEventListener('mouseleave', handleMouseLeave);
      }
    };
  }, []);

  // Update the wheel event listener useEffect
  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });
      return () => container.removeEventListener('wheel', handleWheel);
    }
  }, [activePage]);

  const scrollToPage = (index) => {
    // Prevent scrolling if animation is in progress
    if (isAnimating) return;
    
    setIsAnimating(true);
    setActivePage(index);
    
    // Find the target element to scroll to
    const targetElement = document.getElementById(index === 0 ? 'page-a' : pages[index].id);
    if (targetElement) {
      // Smoothly scroll to the target element without updating URL
      targetElement.scrollIntoView({ behavior: 'smooth' });
    }
    
    // Add animate-section class to the target section for smooth transition
    const sections = document.querySelectorAll('.section');
    sections.forEach((section, i) => {
      if (i === index) {
        section.classList.add('animate-section');
      } else {
        section.classList.remove('animate-section');
      }
      });
      
    // Set active class on navigation dot
    const dots = document.querySelectorAll('.nav-dot');
    dots.forEach((dot, i) => {
      if (i === index) {
        dot.classList.add('active');
      } else {
        dot.classList.remove('active');
      }
    });
      
    // Update container class to reflect current section
    const container = document.querySelector('.container');
    if (container) {
      container.className = 'container';
      if (index === 0) container.classList.add('home-active');
      else if (index === 1) container.classList.add('talent-active');
      else if (index === 2) container.classList.add('services-active');
      else if (index === 3) container.classList.add('testimonials-active');
      else if (index === 4) container.classList.add('contact-active');
        }
        
    // Reset animation flag after transition completes
        setTimeout(() => {
          setIsAnimating(false);
        }, 1000);
  };

  const handlePrevPage = () => {
    if (activePage > 0) {
      setIsAnimating(true);
      scrollToPage(activePage - 1);
      setTimeout(() => {
        setIsAnimating(false);
      }, 1000);
    } else {
      // Optionally cycle to the last page
      setIsAnimating(true);
      scrollToPage(pages.length - 1);
      setTimeout(() => {
        setIsAnimating(false);
      }, 1000);
    }
  };

  const handleNextPage = () => {
    if (activePage < pages.length - 1) {
      setIsAnimating(true);
      scrollToPage(activePage + 1);
      setTimeout(() => {
        setIsAnimating(false);
      }, 1000);
    } else {
      // Optionally cycle to the first page
      setIsAnimating(true);
      scrollToPage(0);
      setTimeout(() => {
        setIsAnimating(false);
      }, 1000);
    }
  };

  const handlePrevCard = () => {
    setActiveCardIndex(prev => (prev > 0 ? prev - 1 : pages[1].talents.length - 1));
  };

  const handleNextCard = () => {
    setActiveCardIndex(prev => (prev < pages[1].talents.length - 1 ? prev + 1 : 0));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const originalButtonText = e.target.querySelector('button[type="submit"]').innerText;
      e.target.querySelector('button[type="submit"]').innerText = 'SENDING...';
      
      const response = await axios.post(`${API_URL}/contact`, formData);
      
      if (response.status === 200) {
        alert('Message sent successfully! We will get back to you soon.');
        setFormData({
          firstName: '',
          email: '',
          subject: '',
          message: ''
        });
      } else {
        alert('Failed to send message. Please try again later.');
      }
      
      e.target.querySelector('button[type="submit"]').innerText = originalButtonText;
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message. Please try again later.');
      e.target.querySelector('button[type="submit"]').innerText = 'SEND MESSAGE';
    }
  };

  const handlePrevSponsor = () => {
    setActiveSponsorIndex(prev => (prev > 0 ? prev - 1 : sponsorsData.length - 3));
  };

  const handleNextSponsor = () => {
    setActiveSponsorIndex(prev => (prev < sponsorsData.length - 3 ? prev + 1 : 0));
  };

  const handlePrevTestimonial = () => {
    setActiveTestimonialIndex(prev => (prev > 0 ? prev - 1 : pages[3].testimonials.length - 1));
  };

  const handleNextTestimonial = () => {
    setActiveTestimonialIndex(prev => (prev < pages[3].testimonials.length - 1 ? prev + 1 : 0));
  };

  // Function to get visible sponsors
  const visibleSponsors = () => {
    return sponsorsData.slice(activeSponsorIndex, activeSponsorIndex + 3);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // Talent section
  const TalentSection = () => {
    const talents = [
      {
        name: 'Ryley',
        image: '/images/ryley-profile.jpg',
        followers: '1.2M Followers',
        category: 'FPS & Strategy Games'
      },
      {
        name: 'Vlakx',
        image: '/images/vlakx-profile.jpg',
        followers: '850K Followers',
        category: 'RPG & Adventure Games'
      },
      {
        name: 'Ian77',
        image: '/images/ian77-profile.jpg',
        followers: '1.5M Followers',
        category: 'Battle Royale & Streaming'
      }
    ];

    return (
      <section className="talent-section" id="talent">
      </section>
    );
  };

  // Handle URL hash navigation
  useEffect(() => {
    // Skip hash-based navigation to keep clean URLs
    // Add smooth-scroll class to container
    const container = document.querySelector('.horizontal-container');
    if (container) {
      container.classList.add('smooth-scroll');
    }
  }, []);

  useEffect(() => {
    // Add class to make scrolling smooth
    document.body.classList.add('smooth-scroll');
    
    // Add smooth-scroll class to horizontal container
    const container = document.querySelector('.horizontal-container');
    if (container) {
      container.classList.add('smooth-scroll');
    }
    
    return () => {
      document.body.classList.remove('smooth-scroll');
      if (container) {
        container.classList.remove('smooth-scroll');
      }
    };
  }, []);

  return (
    <div className="App">
      <CustomCursor />
      <CustomScrollbar />
      <Helmet>
        <title>ThirdRail Avenue - Gaming Influence & Connection</title>
        <meta name="description" content="ThirdRail Avenue represents a network of gaming influencers dedicated to creating unforgettable experiences and meaningful connections." />
      </Helmet>
      
      {/* Clean Navigation Bar */}
      <nav className="top-navbar">
        <div className="navbar-logo">
          <img 
            src={process.env.PUBLIC_URL + '/Image16.png'} 
            alt="ThirdRail Avenue Logo" 
          />
        </div>
        <div className="navbar-links">
          <a href="#home" onClick={(e) => {
            e.preventDefault();
            scrollToPage(0);
          }}>HOME</a>
          <Link to="/talent-roster">TALENT</Link>
          <a href="#services" onClick={(e) => {
            e.preventDefault();
            scrollToPage(2);
          }}>SERVICES</a>
          <a href="#testimonials" onClick={(e) => {
            e.preventDefault();
            scrollToPage(3);
          }}>TESTIMONIALS</a>
          <a href="#contact" onClick={(e) => {
            e.preventDefault();
            scrollToPage(4);
          }}>CONTACT</a>
        </div>
        
        {/* Mobile Menu Button */}
        <button 
          className={`mobile-menu-button ${mobileMenuOpen ? 'open' : ''}`}
          onClick={toggleMobileMenu}
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </nav>
      
      {/* Mobile Menu Overlay */}
      <div 
        className={`mobile-menu-overlay ${mobileMenuOpen ? 'open' : ''}`}
        onClick={() => setMobileMenuOpen(false)}
      ></div>
      
      {/* Mobile Menu */}
      <div className={`mobile-menu ${mobileMenuOpen ? 'open' : ''}`}>
        {pageTitles.map((title, index) => (
          <div 
            key={index}
            className="mobile-menu-item"
            onClick={() => {
              scrollToPage(index);
              setMobileMenuOpen(false);
            }}
          >
            {title}
          </div>
        ))}
      </div>

      {/* Content Sections */}
      <div className="horizontal-container">
        {/* Hero Video Section */}
        <section className={`section hero-section ${activePage === 0 ? 'animate-section' : ''}`} id="page-a">
          <div className="ribbon-background">TRA</div>
          <div className="video-background">
            <video 
              className="background-video" 
              autoPlay 
              loop 
              muted 
              playsInline
              src={process.env.PUBLIC_URL + '/videos/ref.mp4'}
            >
              Your browser does not support the video tag.
            </video>
          </div>
          
          <div className="hero-content">
            <h1 className="mega-title">{pages[0].title}</h1>
            <h2 className="mega-subtitle">{pages[0].subtitle}</h2>
            <p className="sub-text">{pages[0].description}</p>
            <div className="button-container">
              <button onClick={() => scrollToPage(2)} className="submit-button">
                Explore Services
              </button>
              <button onClick={() => scrollToPage(4)} className="submit-button">
                Contact Us Today
              </button>
            </div>
          </div>
        </section>
        
        {/* Sponsors Ribbon */}
        <div className="sponsors-ribbon">
          <div className="sponsors-track">
            {/* First set of logos */}
            <div className="sponsor-logo">
              <img src={process.env.PUBLIC_URL + '/sponsors/Image5.png'} alt="Sponsor" />
            </div>
            <div className="sponsor-logo">
              <img src={process.env.PUBLIC_URL + '/sponsors/Image6.png'} alt="Sponsor" />
            </div>
            <div className="sponsor-logo">
              <img src={process.env.PUBLIC_URL + '/sponsors/Image7.png'} alt="Sponsor" />
            </div>
            <div className="sponsor-logo">
              <img src={process.env.PUBLIC_URL + '/sponsors/Image8.png'} alt="Sponsor" />
            </div>
            <div className="sponsor-logo">
              <img src={process.env.PUBLIC_URL + '/sponsors/Image9.png'} alt="Sponsor" />
            </div>
            <div className="sponsor-logo">
              <img src={process.env.PUBLIC_URL + '/sponsors/Image10.png'} alt="Sponsor" />
            </div>
            <div className="sponsor-logo">
              <img src={process.env.PUBLIC_URL + '/sponsors/Image11.png'} alt="Sponsor" />
            </div>
            <div className="sponsor-logo">
              <img src={process.env.PUBLIC_URL + '/sponsors/Image14.png'} alt="Sponsor" />
            </div>
            {/* Duplicate set for seamless loop */}
            <div className="sponsor-logo">
              <img src={process.env.PUBLIC_URL + '/sponsors/Image5.png'} alt="Sponsor" />
            </div>
            <div className="sponsor-logo">
              <img src={process.env.PUBLIC_URL + '/sponsors/Image6.png'} alt="Sponsor" />
            </div>
            <div className="sponsor-logo">
              <img src={process.env.PUBLIC_URL + '/sponsors/Image7.png'} alt="Sponsor" />
            </div>
            <div className="sponsor-logo">
              <img src={process.env.PUBLIC_URL + '/sponsors/Image8.png'} alt="Sponsor" />
            </div>
            <div className="sponsor-logo">
              <img src={process.env.PUBLIC_URL + '/sponsors/Image9.png'} alt="Sponsor" />
            </div>
            <div className="sponsor-logo">
              <img src={process.env.PUBLIC_URL + '/sponsors/Image10.png'} alt="Sponsor" />
            </div>
            <div className="sponsor-logo">
              <img src={process.env.PUBLIC_URL + '/sponsors/Image11.png'} alt="Sponsor" />
            </div>
            <div className="sponsor-logo">
              <img src={process.env.PUBLIC_URL + '/sponsors/Image14.png'} alt="Sponsor" />
            </div>
          </div>
        </div>

        {/* Talent Cards Section */}
        <section className="talent-section">
          <div className="talent-ribbon">CREATORS</div>
          <div className="talent-container">
            <div className="talent-grid">
              <div className="talent-roster-card">
                <div>
                  <div className="talent-roster-text">
                    MILLIONS<br />
                    THROUGH<br />
                    TRUSTED<br />
                    INFLUENCERS
                  </div>
                  <Link to="/talent-roster" className="submit-button">View Talent Roster</Link>
                </div>
              </div>
              
              <div className="talent-card">
                <div className="talent-image">
                  <img src={process.env.PUBLIC_URL + '/Ian77.png'} alt="Ian77" />
                </div>
                <div className="talent-info">
                  <div className="talent-name">Ian77</div>
                  <div className="talent-followers">1.5M+ REACH</div>
                  <div className="talent-socials">
                    <a href="#"><FaTwitch /></a>
                    <a href="#"><FaTiktok /></a>
                    <a href="#"><FaTwitter /></a>
                  </div>
                </div>
              </div>
              
              <div className="talent-card">
                <div className="talent-image">
                  <img src={process.env.PUBLIC_URL + '/Ryley.png'} alt="Ryley" />
                </div>
                <div className="talent-info">
                  <div className="talent-name">Ryley</div>
                  <div className="talent-followers">1.2M+ REACH</div>
                  <div className="talent-socials">
                    <a href="#"><FaTwitch /></a>
                    <a href="#"><FaYoutube /></a>
                    <a href="#"><FaTwitter /></a>
                  </div>
                </div>
              </div>
              
              <div className="talent-card">
                <div className="talent-image">
                  <img src={process.env.PUBLIC_URL + '/ALT.png'} alt="ALT" />
                </div>
                <div className="talent-info">
                  <div className="talent-name">ALT</div>
                  <div className="talent-followers">200K+ REACH</div>
                  <div className="talent-socials">
                    <a href="#"><FaYoutube /></a>
                    <a href="#"><FaTwitter /></a>
                    <a href="#"><FaInstagram /></a>
                  </div>
                </div>
              </div>
              
              <div className="talent-card">
                <div className="talent-image">
                  <img src={process.env.PUBLIC_URL + '/Vlakx.png'} alt="Vlakx" />
                </div>
                <div className="talent-info">
                  <div className="talent-name">Vlakx</div>
                  <div className="talent-followers">950K+ REACH</div>
                  <div className="talent-socials">
                    <a href="#"><FaYoutube /></a>
                    <a href="#"><FaTwitter /></a>
                    <a href="#"><FaInstagram /></a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* What We Do Section */}
        <section id="what-we-do" className={`section ${activePage === 2 ? 'animate-section' : ''}`}>
          <div className="what-ribbon">SERVICES</div>
          
          <div className="content what-we-do-content">
            <h1 className="section-title">{pages[2].title}</h1>
            
            <div className="card-grid">
              {pages[2].cards.map((card, index) => (
                <div 
                  key={index} 
                  className="flip"
                  onMouseEnter={() => clearInterval(carouselIntervalRef.current)}
                  onMouseLeave={() => {
                    if (carouselIntervalRef.current) clearInterval(carouselIntervalRef.current);
                    carouselIntervalRef.current = setInterval(() => {
                      handleNextCard();
                    }, 5000);
                  }}
                >
                  <div className="front" style={{ backgroundImage: `url(${process.env.PUBLIC_URL + '/texture2.jpg'})` }}>
                    <div className="card-icon">{card.icon}</div>
                    <h1 className="text-shadow">{card.title}</h1>
                  </div>
                  <div className="back">
                    <p>{card.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Success Stories Section */}
        <section key={pages[3].id} id={pages[3].id} className={`section testimonials-section ${activePage === 3 ? 'animate-section' : ''}`}>
          <div className="testimonials-ribbon">SUCCESS</div>
          <div className="content success-stories-content">
            <div className="testimonials-header">
              <h2>WHAT OUR PARTNERS SAY</h2>
              <h3>ABOUT COLLABORATING WITH US</h3>
            </div>
            
            <div className="testimonial-slider-container">
              <div 
                className={`testimonial-slider`} 
                style={{ 
                  transform: `translateX(-${activeTestimonialIndex * 25}%)` 
                }}
              >
                {pages[3].testimonials.map((testimonial, index) => (
                  <div 
                    key={index} 
                    className="testimonial-slide"
                  >
                    <div className="testimonial-card">
                      <div className="testimonial-logo">
                        <img 
                          src={process.env.PUBLIC_URL + testimonial.image} 
                          alt={`${testimonial.name} logo`} 
                        />
                      </div>
                      <div className="testimonial-quote">
                        "{testimonial.quote}"
                      </div>
                      <div className="testimonial-author">
                        <div className="testimonial-name">{testimonial.name}</div>
                        <div className="testimonial-role">{testimonial.role}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section key={pages[4].id} id={pages[4].id} className="section contact-section">
          <div className="contact-ribbon">CONTACT</div>
          <div className="contact-left">
            <div>
              <div className="contact-logo">
              </div>
              <h1 className="contact-title">Contact Us</h1>
            </div>
            
            <div className="contact-footer">
              <div className="contact-info">
                <div className="contact-info-item">
                  <div className="contact-info-label">Follow us</div>
                  <div className="contact-info-value">Instagram • Facebook • Twitter</div>
                </div>
              </div>
            </div>
          </div>

          <div className="contact-right">
            <form action="https://formsubmit.co/collab@thirdrailavenue.com" method="POST" className="contact-form">
              <input type="hidden" name="_next" value={`${window.location.origin}/thank-you`} />
              <input type="hidden" name="_subject" value="New Contact Form Submission" />
              <input type="hidden" name="_captcha" value="false" />
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName">First Name</label>
                  <input
                    type="text"
                    id="firstName"
                    name="name"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="lastName">Last Name</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    required
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="phone">Phone Number</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="company">Company</label>
                <input
                  type="text"
                  id="company"
                  name="company"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="message">Message</label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows="5"
                ></textarea>
              </div>
              
              <button type="submit" className="submit-button">Submit →</button>
            </form>
          </div>
        </section>
      </div>
    </div>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<MainApp />} />
        <Route path="/talent-roster" element={<TalentRoster />} />
        <Route path="/thank-you" element={<ThankYou />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Router>
  );
}

export default App; 
